import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';

const TAG_COLORS = ['#f59e0b','#10b981','#ef4444','#8b5cf6','#06b6d4','#f97316','#84cc16','#ec4899'];

function createNoteCard(note, index) {
  var w = 280, h = 220;
  var canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  var ctx = canvas.getContext('2d');
  var grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, '#1e2035'); grad.addColorStop(1, '#13141c');
  ctx.fillStyle = grad;
  ctx.beginPath(); ctx.roundRect(4, 4, w - 8, h - 8, 12); ctx.fill();
  ctx.strokeStyle = note.color || TAG_COLORS[index % TAG_COLORS.length];
  ctx.lineWidth = 3;
  ctx.beginPath(); ctx.roundRect(4, 4, w - 8, h - 8, 12); ctx.stroke();
  ctx.fillStyle = '#ffffff'; ctx.font = 'bold 18px Inter'; ctx.textAlign = 'left';
  var title = note.title || 'Untitled';
  if (ctx.measureText(title).width > w - 30) {
    while (ctx.measureText(title + '\u2026').width > w - 30 && title.length > 3) title = title.substring(0, title.length - 1);
    title += '\u2026';
  }
  ctx.fillText(title, 18, 44);
  var tagY = 56;
  ctx.font = '11px Inter';
  (note.tags || []).slice(0, 4).forEach(function(t) {
    var tw = ctx.measureText(t).width + 12;
    ctx.fillStyle = 'rgba(255,255,255,0.08)'; ctx.beginPath(); ctx.roundRect(18, tagY, tw, 18, 6); ctx.fill();
    ctx.fillStyle = '#8892b0'; ctx.fillText(t, 24, tagY + 13); tagY += 22;
  });
  var body = (note.body || '').replace(/[\*\*__\*_`#]/g, '').trim().substring(0, 140);
  ctx.fillStyle = '#5a6480'; ctx.font = '11px Inter';
  wrapText(ctx, body, 18, tagY + 8, w - 36, 14);
  var date = note.updatedAt ? new Date(note.updatedAt).toLocaleDateString([], { month: 'short', day: 'numeric' }) : '';
  if (date) { ctx.fillStyle = '#3a4260'; ctx.font = '10px Inter'; ctx.textAlign = 'right'; ctx.fillText(date, w - 18, h - 14); }
  var tex = new THREE.CanvasTexture(canvas); tex.minFilter = THREE.LinearFilter;
  var mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, side: THREE.DoubleSide, depthWrite: true });
  var geom = new THREE.PlaneGeometry(3.2, 2.5);
  var mesh = new THREE.Mesh(geom, mat);
  mesh.userData = { uid: note.uid, label: note.title || 'Untitled', tags: note.tags || [] };
  return mesh;
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  var words = text.split(/\s+/); var line = '';
  for (var n = 0; n < words.length; n++) {
    var tl = line + words[n] + ' ';
    if (ctx.measureText(tl).width > maxWidth && line.length > 0) { ctx.fillText(line.trim(), x, y); line = words[n] + ' '; y += lineHeight; }
    else { line = tl; }
  }
  ctx.fillText(line.trim(), x, y);
}

export default function NotesNetworkView3D({ notes, onOpenNote, onClose, tourActive }) {
  const mountRef = useRef(null);

  useEffect(function() {
    var mount = mountRef.current;
    if (!mount) return;
    var W = mount.clientWidth, H = mount.clientHeight;
    var scene = new THREE.Scene();
    scene.background = new THREE.Color('#0a0a12');
    scene.fog = new THREE.Fog('#0a0a12', 40, 140);
    var camera = new THREE.PerspectiveCamera(50, W / H, 1, 200);
    camera.position.set(0, 6, 35); camera.lookAt(0, 0, 0);
    var renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(W, H); renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    mount.appendChild(renderer.domElement);
    var labelRenderer = new CSS2DRenderer();
    labelRenderer.setSize(W, H); labelRenderer.domElement.style.position = 'absolute'; labelRenderer.domElement.style.top = '0'; labelRenderer.domElement.style.pointerEvents = 'none';
    mount.appendChild(labelRenderer.domElement);
    var controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true; controls.dampingFactor = 0.08; controls.minDistance = 6; controls.maxDistance = 80;
    scene.add(new THREE.AmbientLight(0x556688, 0.8));
    var dir = new THREE.DirectionalLight(0xffffff, 0.5); dir.position.set(15, 20, 10); scene.add(dir);
    var grid = new THREE.PolarGridHelper(30, 24, 12, 48, 0x141428, 0x0e0e1a); scene.add(grid);

    var starGeom = new THREE.BufferGeometry(); var starPos = [];
    for (var i = 0; i < 400; i++) starPos.push((Math.random() - 0.5) * 120, (Math.random() - 0.5) * 80, (Math.random() - 0.5) * 60);
    starGeom.setAttribute('position', new THREE.Float32BufferAttribute(starPos, 3));
    scene.add(new THREE.Points(starGeom, new THREE.PointsMaterial({ color: 0x445577, size: 0.15 })));

    // Build graph
    var cardMeshes = notes.map(function(n, i) {
      var card = createNoteCard(n, i);
      var angle = (i / Math.max(1, notes.length)) * Math.PI * 2;
      var radius = 14 + notes.length * 0.2;
      card.position.set(Math.cos(angle) * radius, (Math.random() - 0.5) * 8, Math.sin(angle) * radius);
      card.lookAt(new THREE.Vector3(0, card.position.y, 0));
      scene.add(card);
      return card;
    });

    // Create tag pill groups on note edges and glowing links
    var edgeObjects = []; // { tag, fromCardIdx, toCardIdx, line, pillFrom, pillTo, createdAt }
    var processed = {};

    notes.forEach(function(a, ai) {
      notes.forEach(function(b, bi) {
        if (ai >= bi) return;
        var shared = (a.tags || []).filter(function(t) { return (b.tags || []).includes(t); });
        if (shared.length === 0) return;

        shared.forEach(function(tag) {
          var key = [Math.min(ai, bi), Math.max(ai, bi), tag].join('|');
          if (processed[key]) return;
          processed[key] = true;

          var cardA = cardMeshes[ai], cardB = cardMeshes[bi];
          var color = TAG_COLORS[ai % TAG_COLORS.length];

          // Tag pills on card edges
          var cardW = 1.6, cardH = 1.25;
          var pillFrom = createTagPill(tag, color);
          pillFrom.position.copy(cardA.position).add(new THREE.Vector3(cardW + 0.6, -cardH + 0.2, 0));
          scene.add(pillFrom);

          var pillTo = createTagPill(tag, color);
          pillTo.position.copy(cardB.position).add(new THREE.Vector3(-cardW - 0.6, -cardH + 0.2, 0));
          scene.add(pillTo);

          // Glowing tube between pills
          var mid = new THREE.Vector3().addVectors(pillFrom.position, pillTo.position).multiplyScalar(0.5);
          var dirVec = new THREE.Vector3().subVectors(pillTo.position, pillFrom.position);
          var len = dirVec.length();
          var tubeGeom = new THREE.CylinderGeometry(0.04, 0.04, 1, 6, 1);
          var tubeMat = new THREE.MeshBasicMaterial({ color: color, transparent: true, opacity: 0.8, depthWrite: true });
          var tube = new THREE.Mesh(tubeGeom, tubeMat);
          tube.position.copy(mid);
          tube.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dirVec.normalize());
          scene.add(tube);

          // Glow ring at midpoint
          var glowGeom = new THREE.TorusGeometry(0.2, 0.04, 8, 16);
          var glowMat = new THREE.MeshBasicMaterial({ color: color, transparent: true, opacity: 0.6 });
          var glow = new THREE.Mesh(glowGeom, glowMat);
          glow.position.copy(mid);
          glow.lookAt(new THREE.Vector3().addVectors(mid, dirVec.normalize()));
          scene.add(glow);

          edgeObjects.push({ tag: tag, fromIdx: ai, toIdx: bi, tube: tube, glow: glow, pillFrom: pillFrom, pillTo: pillTo, color: color, createdAt: Date.now() });
        });
      });
    });

    // Auto-fit: calculate bounding sphere of all cards
    var box = new THREE.Box3();
    cardMeshes.forEach(function(m) { box.expandByObject(m); });
    var fitCenter = new THREE.Vector3();
    box.getCenter(fitCenter);
    var size = new THREE.Vector3();
    box.getSize(size);
    var maxDim = Math.max(size.x, size.y, size.z);
    var fitCameraPos = new THREE.Vector3(fitCenter.x, fitCenter.y + Math.max(10, maxDim * 0.5), fitCenter.z + maxDim * 1.5);
    var fitTarget = true;
    controls.target.copy(fitCenter);

    // Camera tour state
    var tourIdx = 0;
    var tourTimer = 0;
    var tourPause = 0;
    var tourTargetPos = new THREE.Vector3();
    var tourTargetLook = new THREE.Vector3();
    var wasTourActive = false;

    function createTagPill(tag, color) {
      var c = document.createElement('canvas'); c.width = 128; c.height = 32;
      var cx = c.getContext('2d');
      cx.fillStyle = color; cx.beginPath(); cx.roundRect(2, 2, 124, 28, 14); cx.fill();
      cx.fillStyle = '#fff'; cx.font = 'bold 13px Inter'; cx.textAlign = 'center'; cx.textBaseline = 'middle';
      cx.fillText(tag, 64, 16);
      var tex = new THREE.CanvasTexture(c); tex.minFilter = THREE.LinearFilter;
      var mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, side: THREE.DoubleSide, depthWrite: false });
      var geom = new THREE.PlaneGeometry(1.1, 0.28);
      var mesh = new THREE.Mesh(geom, mat);
      mesh.userData = { isPill: true, tag: tag };
      return mesh;
    }

    // Raycaster
    var raycaster = new THREE.Raycaster(); var mouse = new THREE.Vector2();
    var hoveredMesh = null;

    renderer.domElement.addEventListener('mousemove', function(e) {
      var rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      var hits = raycaster.intersectObjects(cardMeshes.concat(edgeObjects.map(function(eo) { return eo.tube; })));
      if (hits.length > 0) {
        var obj = hits[0].object;
        if (hoveredMesh !== obj) {
          if (hoveredMesh && hoveredMesh.material) hoveredMesh.material.emissive && hoveredMesh.material.emissive.set(0x000000);
          hoveredMesh = obj;
          if (obj.material && obj.material.emissive) obj.material.emissive.set(0x333355);
          renderer.domElement.style.cursor = 'pointer';
        }
      } else if (hoveredMesh) {
        if (hoveredMesh.material && hoveredMesh.material.emissive) hoveredMesh.material.emissive.set(0x000000);
        hoveredMesh = null; renderer.domElement.style.cursor = 'grab';
      }
    });

    // Double-click node: open note
    renderer.domElement.addEventListener('dblclick', function(e) {
      var rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      var hits = raycaster.intersectObjects(cardMeshes);
      if (hits.length > 0) {
        var target = hits[0].object;
        if (target.userData && target.userData.uid && onOpenNote) {
          onOpenNote(target.userData.uid);
        }
        e.stopPropagation();
      }
    });

    // Single-click node: select it (highlight)
    var selectedMesh = null;
    var selectedOutline = null;
    renderer.domElement.addEventListener('click', function(e) {
      var rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      var hits = raycaster.intersectObjects(cardMeshes);
      if (hits.length > 0) {
        var obj = hits[0].object;
        if (obj === selectedMesh) return; // already selected
        // Deselect previous
        if (selectedMesh) {
          selectedMesh.material.emissive && selectedMesh.material.emissive.set(0x000000);
          if (selectedOutline) { selectedMesh.remove(selectedOutline); selectedOutline = null; }
        }
        // Select new
        selectedMesh = obj;
        if (obj.material && obj.material.emissive) obj.material.emissive.set(0x4488ff);
        // Add selection ring
        var ringGeo = new THREE.RingGeometry(1.55, 1.65, 64);
        var ringMat = new THREE.MeshBasicMaterial({ color: 0x4488ff, side: THREE.DoubleSide, transparent: true, opacity: 0.7 });
        selectedOutline = new THREE.Mesh(ringGeo, ringMat);
        selectedOutline.position.z = 0.01;
        obj.add(selectedOutline);
        e.stopPropagation();
      } else {
        // Deselect
        if (selectedMesh) {
          selectedMesh.material.emissive && selectedMesh.material.emissive.set(0x000000);
          if (selectedOutline) { selectedMesh.remove(selectedOutline); selectedOutline = null; }
          selectedMesh = null;
        }
      }
    });

    // Node dragging in 3D
    var draggedCard = null;
    var dragPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
    var dragOffset = new THREE.Vector3();

    renderer.domElement.addEventListener('pointerdown', function(e) {
      if (e.button !== 0) return;
      var rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      var hits = raycaster.intersectObjects(cardMeshes);
      if (hits.length > 0) {
        draggedCard = hits[0].object;
        controls.enabled = false;
        dragPlane.setFromNormalAndCoplanarPoint(camera.getWorldDirection(new THREE.Vector3()).negate(), draggedCard.position);
        raycaster.ray.intersectPlane(dragPlane, dragOffset);
        dragOffset.subVectors(draggedCard.position, dragOffset);
        renderer.domElement.style.cursor = 'grabbing';
        e.stopPropagation();
      }
    });

    window.addEventListener('pointermove', function(e) {
      if (!draggedCard) return;
      var rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      var intersection = new THREE.Vector3();
      if (raycaster.ray.intersectPlane(dragPlane, intersection)) {
        draggedCard.position.copy(intersection.add(dragOffset));
      }
    });

    window.addEventListener('pointerup', function() {
      if (draggedCard) {
        draggedCard = null;
        controls.enabled = true;
        renderer.domElement.style.cursor = '';
      }
    });

    function animate() {
      requestAnimationFrame(animate);
      controls.update();

      // Auto-fit camera when notes change: smoothly move toward target
      if (fitTarget) {
        camera.position.lerp(fitCameraPos, 0.08);
        controls.target.lerp(fitCenter, 0.08);
        if (camera.position.distanceTo(fitCameraPos) < 0.5) fitTarget = false;
      }

      // Camera tour: guide through each note card in list order
      if (tourActive && cardMeshes.length > 0) {
        if (!wasTourActive) {
          tourIdx = 0; tourTimer = 0; wasTourActive = true;
        }
        if (tourTimer === 0) {
          // Setup target for current card
          var card = cardMeshes[tourIdx];
          var cardPos = card.position.clone();
          // Position camera in front of card, angled for readability
          var cardNormal = new THREE.Vector3(0, 0, 1);
          cardNormal.applyQuaternion(card.quaternion);
          tourTargetPos.copy(cardPos).add(cardNormal.multiplyScalar(8)).add(new THREE.Vector3(0, 1.5, 0));
          tourTargetLook.copy(cardPos);
          controls.enabled = false;
          fitTarget = false;
          tourTimer = Date.now();
          tourPause = 2000; // pause 2 seconds per card
        }
        // Smoothly move camera to target
        camera.position.lerp(tourTargetPos, 0.06);
        controls.target.lerp(tourTargetLook, 0.06);
        if (camera.position.distanceTo(tourTargetPos) < 0.3 && Date.now() - tourTimer > tourPause) {
          tourTimer = 0;
          tourIdx = (tourIdx + 1) % cardMeshes.length;
        }
      } else if (!tourActive && !fitTarget) {
        // Tour stopped while moving — release controls
        controls.enabled = true;
        wasTourActive = false;
      }

      var now = Date.now();

      // Billboard cards
      cardMeshes.forEach(function(mesh) { mesh.lookAt(camera.position.x, mesh.position.y, camera.position.z); });

      // Update edge objects — pills and tubes stay attached, new edges blink
      edgeObjects.forEach(function(eo) {
        var cardA = cardMeshes[eo.fromIdx], cardB = cardMeshes[eo.toIdx];
        if (!cardA || !cardB) return;

        // Position pills on card edges (relative to card center)
        var cardW = 1.6, cardH = 1.25; // half of 3.2 x 2.5
        var rightA = new THREE.Vector3(cardW + 0.6, -cardH + 0.2, 0);
        rightA.applyQuaternion(cardA.quaternion);
        eo.pillFrom.position.copy(cardA.position).add(rightA);
        eo.pillFrom.quaternion.copy(cardA.quaternion);

        var leftB = new THREE.Vector3(-cardW - 0.6, -cardH + 0.2, 0);
        leftB.applyQuaternion(cardB.quaternion);
        eo.pillTo.position.copy(cardB.position).add(leftB);
        eo.pillTo.quaternion.copy(cardB.quaternion);

        // Update tube
        var pa = eo.pillFrom.position, pb = eo.pillTo.position;
        var mid = new THREE.Vector3().addVectors(pa, pb).multiplyScalar(0.5);
        var dirVec = new THREE.Vector3().subVectors(pb, pa);
        var len = dirVec.length();
        eo.tube.position.copy(mid);
        eo.tube.scale.set(1, len, 1);
        eo.tube.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dirVec.normalize());

        // Glow at midpoint
        eo.glow.position.copy(mid);
        eo.glow.lookAt(new THREE.Vector3().addVectors(mid, dirVec.normalize()));

        // Blink for first 10 seconds
        var age = (now - eo.createdAt) / 1000;
        var blink = age < 10 ? 0.5 + Math.sin(now * 0.01) * 0.5 : 1;
        eo.tube.material.opacity = 0.5 + blink * 0.4;
        eo.glow.material.opacity = 0.3 + blink * 0.4;
      });

      renderer.render(scene, camera);
      labelRenderer.render(scene, camera);
    }
    animate();

    var onResize = function() { W = mount.clientWidth; H = mount.clientHeight; camera.aspect = W / H; camera.updateProjectionMatrix(); renderer.setSize(W, H); labelRenderer.setSize(W, H); };
    var resizeObs = new ResizeObserver(onResize);
    resizeObs.observe(mount);
    return function() { resizeObs.disconnect(); mount.removeChild(renderer.domElement); mount.removeChild(labelRenderer.domElement); renderer.dispose(); };
  }, [notes, onOpenNote]);

  return <div ref={mountRef} className="notes-network-3d" />;
}
